import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Calculator, DoorOpen, LoaderCircle, Ruler, ShieldCheck, ScanEye } from 'lucide-react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { createMeshoptGltfLoader } from '@/features/scene/lib/meshoptGltfLoader';
import { publicSketchupEstimateDisclosure } from './public-sketchup-estimate-status';

type ClientEstimateRow = {
  kind?: 'material' | 'hardware' | 'operation';
  name?: string;
  article?: string;
  qty?: string;
  unit?: string;
  rateRub?: string;
  totalRub?: string;
  confidence?: string;
  note?: string;
};

type PublicSketchupClientResponse = {
  ok: boolean;
  jobId: string;
  exportId: string;
  revisionId: string;
  sourceFilename?: string | null;
  createdAt?: string | null;
  model: { glbUrl: string; artifactSha256: string; byteLength?: number | null } | null;
  motion?: PublicSketchupMotion | null;
  orientation?: PublicSketchupOrientation | null;
  estimate: {
    status?: string | null;
    total?: string | null;
    currency?: string | null;
    rows: ClientEstimateRow[];
    partGroups: Record<string, { count?: number; areaM2?: string; edgeM?: string }>;
    hardwarePhysical: Record<string, unknown>;
    sourceDefaultedGroups?: { count?: number };
    unpricedSourceGroups?: { count?: number; complete?: boolean; rows?: unknown[] };
  };
};

type PublicSketchupMotionGroup = {
  id: string;
  type: 'hinged' | 'drawer' | 'dropFront';
  authority: 'source_native_hardware_projection' | 'source_native_drawer_structure' | string;
  movingSourceIndices: number[];
  frontSourceIndices?: number[];
  bodySourceIndices?: number[];
  hingeSourceIndices?: number[];
  runnerSourceIndices?: number[];
  referenceSourceIndices?: number[];
  axis?: 'x' | 'y' | 'z' | string;
  openAngleDeg?: number;
  travelMm?: number;
  directionWorld?: [number, number, number];
};

type PublicSketchupMotion = {
  schema: 'holzcom.public-sketchup-motion.v1' | string;
  authority: 'source_native_hardware_projection' | string;
  hingedGroups: PublicSketchupMotionGroup[];
  drawerGroups: PublicSketchupMotionGroup[];
  dropFrontGroups?: PublicSketchupMotionGroup[];
  lightingGroups?: PublicSketchupLightingGroup[];
  hiddenSourceIndices?: number[];
  summary?: { hingedGroups?: number; drawerGroups?: number; dropFrontGroups?: number; lightingGroups?: number; hiddenSourceRows?: number; sourceRows?: number };
};

type PublicSketchupLightingGroup = {
  id: string;
  type: 'linearLight' | string;
  authority: 'source_native_lighting_projection' | string;
  sourceIndices: number[];
  color?: string;
  temperatureK?: number;
};

type PublicSketchupOrientationGroup = {
  type: 'rotateY' | string;
  angleDeg?: number;
  pivotMm?: number[];
  sourceIndices: number[];
};

type PublicSketchupOrientation = {
  schema: 'holzcom.public-sketchup-orientation.v1' | string;
  authority: 'island_orientation_policy' | string;
  groups: PublicSketchupOrientationGroup[];
};

export function publicSketchupClientRoute(): { jobId: string; exportId: string } | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/^\/client\/sketchup\/([^/]+)\/([^/]+)\/?$/);
  if (!match) return null;
  return { jobId: decodeURIComponent(match[1]!), exportId: decodeURIComponent(match[2]!) };
}

const PUBLIC_SKETCHUP_FETCH_RETRY_DELAYS_MS = [300, 900];
const PUBLIC_SKETCHUP_NETWORK_ERROR = 'Не удалось загрузить модель и смету: сетевой сбой. Обновите страницу или откройте ссылку ещё раз.';

type PublicSketchupFetchOptions = {
  fetchImpl?: typeof fetch;
  retryDelaysMs?: readonly number[];
};

function wait(ms: number): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetryPublicSketchupFetch(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  const status = error instanceof Error ? Number(error.message.match(/\((\d{3})\)/)?.[1]) : NaN;
  return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

export async function fetchPublicSketchupClient(
  target: { jobId: string; exportId: string },
  options: PublicSketchupFetchOptions = {},
): Promise<PublicSketchupClientResponse> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const retryDelaysMs = options.retryDelaysMs ?? PUBLIC_SKETCHUP_FETCH_RETRY_DELAYS_MS;
  const url = `/api/public/sketchup-client/${encodeURIComponent(target.jobId)}/${encodeURIComponent(target.exportId)}`;
  let lastError: unknown;
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    try {
      const response = await fetchImpl(url, {
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Модель недоступна (${response.status})`);
      return response.json() as Promise<PublicSketchupClientResponse>;
    } catch (error) {
      lastError = error;
      if (!shouldRetryPublicSketchupFetch(error) || attempt >= retryDelaysMs.length) break;
      await wait(retryDelaysMs[attempt] ?? 0);
    }
  }
  if (lastError instanceof TypeError) throw new Error(PUBLIC_SKETCHUP_NETWORK_ERROR);
  throw lastError instanceof Error ? lastError : new Error('Не удалось открыть клиентскую ссылку.');
}

export function PublicSketchupClientView() {
  const route = useRef(publicSketchupClientRoute());
  const [tab, setTab] = useState<'model' | 'estimate'>('model');
  const [state, setState] = useState<{ loading: boolean; value?: PublicSketchupClientResponse; error?: string }>({ loading: true });

  useEffect(() => {
    const target = route.current;
    if (!target) {
      setState({ loading: false, error: 'Клиентская ссылка на модель некорректна.' });
      return;
    }
    let cancelled = false;
    void fetchPublicSketchupClient(target)
      .then((value) => {
        if (cancelled) return;
        setState({ loading: false, value });
        // КП без GLB-артефакта (PRO100: JSON-сцена) открывается сразу на смете.
        if (!value.model) setTab('estimate');
      })
      .catch((error: unknown) => { if (!cancelled) setState({ loading: false, error: error instanceof Error ? error.message : 'Не удалось открыть клиентскую ссылку.' }); });
    return () => { cancelled = true; };
  }, []);

  if (state.loading) {
    return <ClientShell><div className="client-card client-state"><LoaderCircle className="spin" /> Загружаю модель и смету…</div></ClientShell>;
  }
  if (state.error || !state.value) {
    return <ClientShell><div className="client-card client-state error">{state.error || 'Нет данных клиентской модели.'}</div></ClientShell>;
  }
  const disclosure = publicSketchupEstimateDisclosure(
    state.value.estimate.status,
    state.value.estimate.unpricedSourceGroups,
    state.value.estimate.sourceDefaultedGroups,
  );
  return (
    <ClientShell>
      <header className="client-header">
        <div className="brand">
          <span className="brand-icon"><Box size={18} /></span>
          <div>
            <div className="brand-title">HOLZCOM · клиентский просмотр</div>
            <div className="brand-subtitle">{state.value.sourceFilename || '3D-модель'} · КП {state.value.exportId.slice(0, 8)}</div>
          </div>
        </div>
        <div className="client-total">
          <span>{disclosure.headline}</span>
          <strong>{disclosure.showTotal ? `${money(state.value.estimate.total)} ₽` : '—'}</strong>
          {/* 28.09.2026 — премиум-фаза 4: дата состава на экране клиента —
              вопрос «насколько свежая цифра» закрывается там, где её читают. */}
          {composeDate(state.value.createdAt) && <span className="composed-at">Составлено {composeDate(state.value.createdAt)}</span>}
        </div>
      </header>
      <nav className="client-tabs" aria-label="Клиентские вкладки">
        {state.value.model && (
          <button type="button" className={tab === 'model' ? 'active' : ''} onClick={() => setTab('model')}><Box size={16} /> Модель</button>
        )}
        <button type="button" className={tab === 'estimate' ? 'active' : ''} onClick={() => setTab('estimate')}><Calculator size={16} /> Смета</button>
        {/* 28.09.2026 — премиум-фаза 4: PDF через печать браузера; серверного
            рендера КП в контракте нет — не выдумываем. */}
        <button type="button" className="print-pdf" onClick={() => window.print()}>Сохранить PDF</button>
        <span className="readonly"><ShieldCheck size={15} /> только просмотр</span>
      </nav>
      <section className="client-body">
        {tab === 'model' && state.value.model
          ? <ClientGlbViewer url={state.value.model.glbUrl} motion={state.value.motion ?? null} orientation={state.value.orientation ?? null} />
          : <ClientEstimate value={state.value} disclosure={disclosure} />}
      </section>
    </ClientShell>
  );
}

function ClientShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="client-page">
      <style>{clientCss}</style>
      {children}
    </main>
  );
}

function ClientGlbViewer({ url, motion, orientation }: { url: string; motion: PublicSketchupMotion | null; orientation: PublicSketchupOrientation | null }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const runtimeRef = useRef<ClientViewerRuntime | null>(null);
  const [status, setStatus] = useState('Загружаю 3D…');
  const [xray, setXray] = useState(false);
  const [dimensions, setDimensions] = useState(false);
  const [openedFacades, setOpenedFacades] = useState(false);
  const [openedDrawers, setOpenedDrawers] = useState(false);
  const [facadeMotionCount, setFacadeMotionCount] = useState(0);
  const [drawerMotionCount, setDrawerMotionCount] = useState(0);

  useEffect(() => {
    applyXray(runtimeRef.current, xray);
  }, [xray]);

  useEffect(() => {
    applyDimensions(runtimeRef.current, dimensions);
  }, [dimensions]);

  useEffect(() => {
    applyOpening(runtimeRef.current, 'facades', openedFacades);
  }, [openedFacades]);

  useEffect(() => {
    applyOpening(runtimeRef.current, 'drawers', openedDrawers);
  }, [openedDrawers]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    runtimeRef.current = null;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(host.clientWidth || 800, host.clientHeight || 600);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.96;
    renderer.shadowMap.enabled = true;
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#edf3ef');
    scene.fog = new THREE.Fog('#edf3ef', 9000, 22000);
    const camera = new THREE.PerspectiveCamera(38, Math.max(1, host.clientWidth) / Math.max(1, host.clientHeight), 1, 100000);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.target.set(0, 700, 0);

    scene.add(new THREE.HemisphereLight('#ffffff', '#bac7bf', 1.08));
    const key = new THREE.DirectionalLight('#fff7ea', 1.28);
    key.position.set(4500, 7000, 5000);
    scene.add(key);
    const fill = new THREE.DirectionalLight('#eaf4ff', 0.42);
    fill.position.set(-5000, 3500, -3500);
    scene.add(fill);
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(6000, 96),
      new THREE.MeshStandardMaterial({ color: '#dfe7df', roughness: 0.82, metalness: 0 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -6;
    scene.add(floor);

    let model: THREE.Object3D | null = null;
    let stopped = false;
    // Декодер подключён всегда: клиентский GLB публикуется сжатым
    // (EXT_meshopt_compression от SKP-воркера), а старые замороженные экспорты
    // без расширения грузятся тем же путём — декодер для несжатых GLB безвреден.
    createMeshoptGltfLoader().load(
      url,
      (gltf) => {
        if (stopped) return;
        model = gltf.scene;
        enhanceClientMaterials(model, renderer.capabilities.getMaxAnisotropy());
        mirrorDecalUvs(model);
        // Остров, нарисованный в исходнике фасадами к стене, разворачивается
        // ДО подгонки камеры: рамки, свет, размеры и траектории читают уже
        // перевёрнутые мировые позиции.
        applySourceOrientation(model, orientation);
        scene.add(model);
        const box = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        const radius = Math.max(size.x, size.y, size.z, 1000);
        const dimensionsGroup = createDimensionOverlay();
        for (const child of createModuleDimensionOverlays(model, center, radius)) {
          dimensionsGroup.add(child);
        }
        scene.add(dimensionsGroup);
        const runtime = collectClientViewerRuntime({
          model,
          scene,
          dimensionsGroup,
          modelCenter: center,
          radius,
          motion,
        });
        runtimeRef.current = runtime;
        setFacadeMotionCount(runtime.openingTargets.filter((target) => target.kind !== 'drawer').length);
        setDrawerMotionCount(runtime.openingTargets.filter((target) => target.kind === 'drawer').length);
        applyXray(runtime, xray);
        applyDimensions(runtime, dimensions);
        applyOpening(runtime, 'facades', openedFacades);
        applyOpening(runtime, 'drawers', openedDrawers);
        controls.target.copy(center);
        camera.position.set(center.x + radius * 0.9, center.y + radius * 0.55, center.z + radius * 1.25);
        camera.near = Math.max(1, radius / 1000);
        camera.far = Math.max(100000, radius * 10);
        camera.updateProjectionMatrix();
        controls.update();
        setStatus('Потяните мышью для обзора · колесо — масштаб');
      },
      undefined,
      () => setStatus('Не удалось загрузить 3D-модель.'),
    );

    const resize = () => {
      const width = host.clientWidth || 800;
      const height = host.clientHeight || 600;
      renderer.setSize(width, height);
      camera.aspect = width / Math.max(1, height);
      camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    const animate = () => {
      if (stopped) return;
      controls.update();
      updateOpeningAnimation(runtimeRef.current);
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };
    animate();
    return () => {
      stopped = true;
      runtimeRef.current = null;
      setFacadeMotionCount(0);
      setDrawerMotionCount(0);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      if (model) scene.remove(model);
      host.replaceChildren();
    };
  }, [url, motion, orientation]);

  return (
    <div className="model-panel">
      <div className="model-chrome">
        <div className="model-title">
          <span>Интерактивная модель</span>
          <strong>{facadeMotionCount + drawerMotionCount} открываемых групп</strong>
        </div>
        <div className="model-tools" aria-label="Инструменты просмотра модели">
          <button
            type="button"
            className={openedFacades ? 'active' : ''}
            disabled={facadeMotionCount === 0}
            title={facadeMotionCount === 0 ? 'В этой SketchUp-модели нет source-native motion contract для открытия фасадов.' : `Открыть фасады · ${facadeMotionCount} source-групп`}
            onClick={() => setOpenedFacades((value) => !value)}
          ><DoorOpen size={16} /> Фасады</button>
          <button
            type="button"
            className={openedDrawers ? 'active' : ''}
            disabled={drawerMotionCount === 0}
            title={drawerMotionCount === 0 ? 'В этой SketchUp-модели нет source-native drawer motion.' : `Открыть ящики · ${drawerMotionCount} source-групп`}
            onClick={() => setOpenedDrawers((value) => !value)}
          ><Box size={16} /> Ящики</button>
          <button type="button" className={dimensions ? 'active' : ''} onClick={() => setDimensions((value) => !value)}><Ruler size={16} /> Размеры</button>
          <button type="button" className={xray ? 'active' : ''} onClick={() => setXray((value) => !value)}><ScanEye size={16} /> X-Ray</button>
        </div>
      </div>
      <div ref={hostRef} className="model-canvas" />
      <div className="model-hint">{status}</div>
    </div>
  );
}

type MaterialSnapshot = {
  mesh: THREE.Mesh;
  material: THREE.Material | THREE.Material[];
};

type OpeningTarget = {
  kind: 'hinged' | 'drawer' | 'dropFront';
  closed: Array<{ object: THREE.Object3D; position: THREE.Vector3; quaternion: THREE.Quaternion }>;
  pivotWorld: THREE.Vector3;
  axisWorld: THREE.Vector3;
  openMagnitude: number;
  delay: number;
};

type ClientViewerRuntime = {
  scene: THREE.Scene;
  dimensionsGroup: THREE.Group;
  lightingGroup: THREE.Group;
  materials: MaterialSnapshot[];
  openingTargets: OpeningTarget[];
  openingProgress: { facades: number; drawers: number };
  openingGoal: { facades: number; drawers: number };
};

function collectClientViewerRuntime({ model, scene, dimensionsGroup, modelCenter, radius, motion }: {
  model: THREE.Object3D;
  scene: THREE.Scene;
  dimensionsGroup: THREE.Group;
  modelCenter: THREE.Vector3;
  radius: number;
  motion: PublicSketchupMotion | null;
}): ClientViewerRuntime {
  const materials: MaterialSnapshot[] = [];
  const bySourceIndex = new Map<number, THREE.Object3D[]>();
  model.updateMatrixWorld(true);
  model.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    materials.push({ mesh: object, material: object.material });
    const sourceIndex = sourceIndexFromObject(object);
    if (sourceIndex !== null) {
      const bucket = bySourceIndex.get(sourceIndex);
      if (bucket) bucket.push(object);
      else bySourceIndex.set(sourceIndex, [object]);
    }
  });
  hideSourceObjects(motion?.hiddenSourceIndices, bySourceIndex);
  const openingTargets = buildSourceMotionTargets(motion, bySourceIndex, scene, modelCenter, radius);
  const lightingGroup = buildSourceLighting(motion, bySourceIndex, radius);
  scene.add(lightingGroup);
  return {
    scene,
    dimensionsGroup,
    lightingGroup,
    materials,
    openingTargets,
    openingProgress: { facades: 0, drawers: 0 },
    openingGoal: { facades: 0, drawers: 0 },
  };
}

function sourceIndexFromObject(object: THREE.Object3D): number | null {
  const match = String(object.name || '').match(/^mesh_(\d+)_/);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function objectsBySource(indices: number[] | undefined, bySourceIndex: Map<number, THREE.Object3D[]>): THREE.Object3D[] {
  const result: THREE.Object3D[] = [];
  const seen = new Set<THREE.Object3D>();
  for (const index of indices ?? []) {
    for (const object of bySourceIndex.get(index) ?? []) {
      if (seen.has(object)) continue;
      seen.add(object);
      result.push(object);
    }
  }
  return result;
}

function hideSourceObjects(indices: number[] | undefined, bySourceIndex: Map<number, THREE.Object3D[]>): void {
  for (const object of objectsBySource(indices, bySourceIndex)) {
    object.visible = false;
  }
}

function applySourceOrientation(model: THREE.Object3D, orientation: PublicSketchupOrientation | null): void {
  if (!orientation || orientation.schema !== 'holzcom.public-sketchup-orientation.v1') return;
  const bySourceIndex = new Map<number, THREE.Object3D[]>();
  model.updateMatrixWorld(true);
  model.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const sourceIndex = sourceIndexFromObject(object);
    if (sourceIndex === null) return;
    const bucket = bySourceIndex.get(sourceIndex);
    if (bucket) bucket.push(object);
    else bySourceIndex.set(sourceIndex, [object]);
  });
  for (const group of orientation.groups ?? []) {
    if (group.type !== 'rotateY') continue;
    const objects = objectsBySource(group.sourceIndices, bySourceIndex);
    if (!objects.length) continue;
    const pivot = Array.isArray(group.pivotMm) ? group.pivotMm : [0, 0, 0];
    if (!pivot.every((value) => Number.isFinite(value))) continue;
    const anchor = new THREE.Group();
    anchor.name = 'holzcom-source-orientation';
    anchor.position.set(pivot[0] ?? 0, pivot[1] ?? 0, pivot[2] ?? 0);
    model.add(anchor);
    for (const object of objects) anchor.attach(object);
    anchor.rotation.y = ((Number(group.angleDeg) || 180) * Math.PI) / 180;
  }
  model.updateMatrixWorld(true);
}

function worldBoxOf(objects: THREE.Object3D[]): THREE.Box3 | null {
  const box = new THREE.Box3();
  let seeded = false;
  for (const object of objects) {
    const itemBox = new THREE.Box3().setFromObject(object);
    if (itemBox.isEmpty()) continue;
    if (!seeded) {
      box.copy(itemBox);
      seeded = true;
    } else {
      box.union(itemBox);
    }
  }
  return seeded ? box : null;
}

function worldCenterOf(objects: THREE.Object3D[], fallback: THREE.Vector3): THREE.Vector3 {
  if (!objects.length) return fallback.clone();
  const box = worldBoxOf(objects);
  if (!box) return fallback.clone();
  const center = new THREE.Vector3();
  box.getCenter(center);
  return center;
}

function chooseHingedMagnitude(
  pivotWorld: THREE.Vector3,
  movingCenter: THREE.Vector3,
  referenceCenter: THREE.Vector3,
  angle: number,
): number {
  const axis = new THREE.Vector3(0, 1, 0);
  const baseVector = movingCenter.clone().sub(pivotWorld);
  const positiveCenter = baseVector.clone().applyAxisAngle(axis, angle).add(pivotWorld);
  const negativeCenter = baseVector.clone().applyAxisAngle(axis, -angle).add(pivotWorld);
  return positiveCenter.distanceTo(referenceCenter) >= negativeCenter.distanceTo(referenceCenter) ? angle : -angle;
}

function buildDropFrontTarget(group: PublicSketchupMotionGroup, objects: THREE.Object3D[], sequence: number): OpeningTarget | null {
  const box = worldBoxOf(objects);
  if (!box) return null;
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  const axisWorld = Math.abs(size.x) >= Math.abs(size.z) ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1);
  const pivotWorld = center.clone();
  pivotWorld.y = box.min.y;
  const angle = THREE.MathUtils.degToRad(Math.min(88, Math.max(28, Number(group.openAngleDeg) || 68)));
  const topPoint = center.clone();
  topPoint.y = box.max.y;
  const positiveTop = topPoint.clone().sub(pivotWorld).applyAxisAngle(axisWorld, angle).add(pivotWorld);
  const negativeTop = topPoint.clone().sub(pivotWorld).applyAxisAngle(axisWorld, -angle).add(pivotWorld);
  return {
    kind: 'dropFront',
    closed: closedState(objects),
    pivotWorld,
    axisWorld,
    openMagnitude: positiveTop.y <= negativeTop.y ? angle : -angle,
    delay: Math.min(sequence * 0.055, 0.34),
  };
}

function closedState(objects: THREE.Object3D[]): OpeningTarget['closed'] {
  return objects.map((object) => ({
    object,
    position: object.position.clone(),
    quaternion: object.quaternion.clone(),
  }));
}

export function buildSourceMotionTargets(
  motion: PublicSketchupMotion | null,
  bySourceIndex: Map<number, THREE.Object3D[]>,
  _scene: THREE.Scene,
  modelCenter: THREE.Vector3,
  radius: number,
): OpeningTarget[] {
  if (!motion || motion.schema !== 'holzcom.public-sketchup-motion.v1') return [];
  const targets: OpeningTarget[] = [];
  let sequence = 0;
  for (const group of motion.hingedGroups ?? []) {
    const objects = objectsBySource(group.movingSourceIndices, bySourceIndex).filter((object) => !isLightingObject(object));
    if (!objects.length) continue;
    const hingeObjects = objectsBySource(group.hingeSourceIndices, bySourceIndex);
    const pivotWorld = worldCenterOf(hingeObjects, worldCenterOf(objects, modelCenter));
    const movingCenter = worldCenterOf(objects, modelCenter);
    const referenceObjects = objectsBySource(group.referenceSourceIndices, bySourceIndex).filter((object) => !isLightingObject(object));
    const referenceCenter = worldCenterOf(referenceObjects, modelCenter);
    const angle = THREE.MathUtils.degToRad(Math.min(105, Math.max(26, Number(group.openAngleDeg) || 72)));
    targets.push({
      kind: 'hinged',
      closed: closedState(objects),
      pivotWorld,
      axisWorld: new THREE.Vector3(0, 1, 0),
      openMagnitude: chooseHingedMagnitude(pivotWorld, movingCenter, referenceCenter, angle),
      delay: Math.min(sequence++ * 0.055, 0.34),
    });
  }
  for (const group of motion.drawerGroups ?? []) {
    const objects = objectsBySource(group.movingSourceIndices, bySourceIndex).filter((object) => !isLightingObject(object));
    if (!objects.length) continue;
    const center = worldCenterOf(objects, modelCenter);
    const frontObjects = objectsBySource(group.frontSourceIndices, bySourceIndex).filter((object) => !isLightingObject(object));
    const bodyObjects = objectsBySource(group.bodySourceIndices, bySourceIndex).filter((object) => !isLightingObject(object));
    const frontCenter = worldCenterOf(frontObjects, center);
    const frontBox = worldBoxOf(frontObjects);
    if (!isUsableDrawerTarget(objects, frontBox)) continue;
    if (!frontBox) continue;
    const bodyCenter = worldCenterOf(bodyObjects, center);
    const runnerObjects = objectsBySource(group.runnerSourceIndices, bySourceIndex).filter((object) => !isLightingObject(object));
    const runnerBox = worldBoxOf(runnerObjects);
    const authoredDirection = group.directionWorld;
    if (authoredDirection && (authoredDirection.length !== 3 || !authoredDirection.every(Number.isFinite)
      || Math.hypot(...authoredDirection) < 0.99)) throw new Error('SKP_DRAWER_DIRECTION_INVALID');
    const direction = authoredDirection ? new THREE.Vector3(...authoredDirection).normalize() : inferDrawerDirection({
      frontCenter,
      frontBox,
      bodyCenter,
      runnerBox,
      drawerCenter: center,
      modelCenter,
    });
    const bodySize = worldBoxOf(bodyObjects)?.getSize(new THREE.Vector3());
    if (!bodySize) continue;
    const bodyDepth = Math.abs(direction.x) * bodySize.x + Math.abs(direction.z) * bodySize.z;
    if (!Number.isFinite(bodyDepth) || bodyDepth <= 0) continue;
    targets.push({
      kind: 'drawer',
      closed: closedState(objects),
      pivotWorld: center,
      axisWorld: direction,
      openMagnitude: Math.min(Math.max(Number(group.travelMm) || radius * 0.07, 1), bodyDepth * 0.8),
      delay: Math.min(sequence++ * 0.055, 0.34),
    });
  }
  for (const group of motion.dropFrontGroups ?? []) {
    const objects = objectsBySource(group.movingSourceIndices, bySourceIndex).filter((object) => !isLightingObject(object));
    if (!objects.length) continue;
    const target = buildDropFrontTarget(group, objects, sequence);
    if (!target) continue;
    targets.push(target);
    sequence += 1;
  }
  return targets;
}

function isUsableDrawerTarget(objects: THREE.Object3D[], frontBox: THREE.Box3 | null): boolean {
  if (objects.length < 2) return false;
  return isUsableDrawerFrontBox(frontBox);
}

function isUsableDrawerFrontBox(box: THREE.Box3 | null): boolean {
  if (!box) return false;
  const size = new THREE.Vector3();
  box.getSize(size);
  const dims = [Math.abs(size.x), Math.abs(size.y), Math.abs(size.z)].sort((a, b) => a - b);
  return dims[1] >= 90 && dims[2] >= 180;
}

function inferDrawerDirection({ frontCenter, frontBox, bodyCenter, runnerBox, drawerCenter, modelCenter }: {
  frontCenter: THREE.Vector3;
  frontBox: THREE.Box3 | null;
  bodyCenter: THREE.Vector3;
  runnerBox: THREE.Box3 | null;
  drawerCenter: THREE.Vector3;
  modelCenter: THREE.Vector3;
}): THREE.Vector3 {
  const frontVector = frontCenter.clone().sub(bodyCenter);
  frontVector.y = 0;
  const outward = drawerCenter.clone().sub(modelCenter);
  outward.y = 0;
  let direction: THREE.Vector3;
  if (frontBox) {
    const frontSize = new THREE.Vector3();
    frontBox.getSize(frontSize);
    direction = frontSize.x <= frontSize.z ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1);
    const signProbe = frontVector.lengthSq() >= 1 ? frontVector : outward;
    if (signProbe.lengthSq() >= 1 && direction.dot(signProbe) < 0) direction.negate();
  } else if (runnerBox) {
    const runnerSize = new THREE.Vector3();
    runnerBox.getSize(runnerSize);
    direction = runnerSize.x >= runnerSize.z ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1);
    const signProbe = frontVector.lengthSq() >= 1 ? frontVector : outward;
    if (signProbe.lengthSq() >= 1 && direction.dot(signProbe) < 0) direction.negate();
  } else {
    direction = frontVector.lengthSq() >= 1 ? frontVector : outward;
  }
  direction.y = 0;
  if (direction.lengthSq() < 1) direction.set(0, 0, 1);
  return direction.normalize();
}

function buildSourceLighting(
  motion: PublicSketchupMotion | null,
  bySourceIndex: Map<number, THREE.Object3D[]>,
  radius: number,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'holzcom-source-lighting';
  if (!motion || motion.schema !== 'holzcom.public-sketchup-motion.v1') return group;
  const lightingGroups = motion.lightingGroups ?? [];
  for (const lighting of lightingGroups) {
    const objects = objectsBySource(lighting.sourceIndices, bySourceIndex);
    if (!objects.length) continue;
    const box = new THREE.Box3();
    let seeded = false;
    for (const object of objects) {
      hideLightingSourceObject(object);
      const itemBox = new THREE.Box3().setFromObject(object);
      if (itemBox.isEmpty()) continue;
      if (!seeded) {
        box.copy(itemBox);
        seeded = true;
      } else {
        box.union(itemBox);
      }
    }
    if (!seeded) continue;
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);
    group.add(createSoftLightWash(center, size, lighting.color || '#fff1ad', radius));
  }
  return group;
}

function isLightEmitterMesh(mesh: THREE.Mesh): boolean {
  const name = `${mesh.name || ''} ${materialLabel(mesh.material)}`.toLowerCase();
  return /light_diffuser|5000k|4000k|6000k|7000k|diffuser/.test(name);
}

function isLightingObject(object: THREE.Object3D): boolean {
  let result = false;
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    if (isLightEmitterMesh(child)) result = true;
  });
  return result;
}

function hideLightingSourceObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.visible = false;
  });
}

function createSoftLightWash(center: THREE.Vector3, size: THREE.Vector3, color: string, radius: number): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 192;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(256, 96, 6, 256, 96, 254);
    gradient.addColorStop(0, 'rgba(255,244,190,.22)');
    gradient.addColorStop(0.38, 'rgba(255,235,150,.075)');
    gradient.addColorStop(1, 'rgba(255,235,150,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const width = Math.min(Math.max(Math.max(size.x, size.z), 420), Math.max(radius * 0.32, 900));
  const depth = Math.min(Math.max(Math.min(Math.max(size.x, size.z) * 0.24, 260), 120), 420);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    color,
    transparent: true,
    opacity: 0.42,
    depthWrite: false,
    depthTest: true,
    side: THREE.DoubleSide,
    blending: THREE.NormalBlending,
  });
  const plane = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), material);
  plane.name = 'source-light-wash';
  plane.position.set(center.x, center.y - Math.max(size.y * 0.45, 24), center.z);
  plane.rotation.x = -Math.PI / 2;
  plane.renderOrder = 1;
  return plane;
}

function materialLabel(material: THREE.Material | THREE.Material[]): string {
  if (Array.isArray(material)) return material.map((item) => item.name || '').join(' ');
  return material.name || '';
}

const DECAL_MATERIAL_NAME = /^(?:push|labels[_-]?[^_]*|blum_label.*)$/;

function mirrorDecalUvs(model: THREE.Object3D): void {
  // Декали исходника (push-наклейки, шильдики labels_*/blum_label) приходят из
  // SKP с зеркальными UV — владелец видит «hsup» вместо «push».  Текстурные
  // трансформы (repeat.x=-1) рендер на этих материалах не применяет, поэтому
  // переворачиваем сами UV-координаты вершин: u' = -u (wrapS уже Repeat).
  model.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    const isDecal = materials.some((material) => DECAL_MATERIAL_NAME.test(String(material?.name || '').toLowerCase()) && (material as THREE.MeshStandardMaterial).map);
    if (!isDecal) return;
    const uv = object.geometry.getAttribute('uv') as THREE.BufferAttribute | undefined;
    if (!uv || (object as THREE.Mesh & { __decalUvMirrored?: boolean }).__decalUvMirrored) return;
    for (let i = 0; i < uv.count; i += 1) uv.setX(i, -uv.getX(i));
    uv.needsUpdate = true;
    (object as THREE.Mesh & { __decalUvMirrored?: boolean }).__decalUvMirrored = true;
  });
}

function enhanceClientMaterials(model: THREE.Object3D, anisotropy: number): void {
  const seen = new Set<THREE.Material>();
  model.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const objectLabel = String(object.name || '').toLowerCase();
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      if (!material || seen.has(material)) continue;
      seen.add(material);
      const named = material as THREE.MeshStandardMaterial;
      const label = `${String(material.name || '').toLowerCase()} ${objectLabel}`;
      const hasMap = Boolean(named.map);
      if (named.map) {
        named.map.colorSpace = THREE.SRGBColorSpace;
        named.map.anisotropy = Math.max(named.map.anisotropy || 1, Math.min(anisotropy || 1, 8));
        named.map.needsUpdate = true;
      }
      if (named.normalMap) named.normalMap.needsUpdate = true;
      if (named.roughness == null || named.roughness > 0.92) named.roughness = 0.72;
      if (/h1369|дуб|каселла|drevis|wood/.test(label)) {
        named.color?.set(hasMap ? '#ffffff' : '#d0a77a');
        named.roughness = 0.62;
      } else if (/w1000|белый|white|лдсп|hdf|хдф/.test(label)) {
        named.color?.set(hasMap ? '#ffffff' : '#f3f0e7');
        named.roughness = 0.68;
      } else if (/u999|ч[её]рн|black|charcoal|пластик ч/.test(label)) {
        named.color?.set(hasMap ? '#ffffff' : '#16181b');
        named.roughness = 0.5;
      } else if (/стекло|glass|translucent/.test(label)) {
        named.color?.set('#c8d9d8');
        named.transparent = true;
        named.opacity = Math.min(named.opacity ?? 0.62, 0.62);
        named.roughness = 0.18;
        named.metalness = 0;
      } else if (/металл|metal|хром|алюмини|ручка|handle/.test(label)) {
        named.color?.set('#9ca3a5');
        named.metalness = 0.62;
        named.roughness = 0.28;
      } else if (/light_diffuser|5000k|4000k|6000k|7000k|diffus/.test(label)) {
        named.color?.set('#fff7d2');
        named.emissive?.set('#fff1ad');
        named.emissiveIntensity = 0.16;
      } else if (!hasMap && isOverSaturatedYellow(named)) {
        named.color?.set('#d8cfad');
        named.roughness = 0.74;
        named.metalness = 0;
      }
      named.needsUpdate = true;
    }
  });
}

function isOverSaturatedYellow(material: THREE.MeshStandardMaterial): boolean {
  if (!material.color) return false;
  const hsl = { h: 0, s: 0, l: 0 };
  material.color.getHSL(hsl);
  return hsl.h >= 0.10 && hsl.h <= 0.19 && hsl.s > 0.28 && hsl.l > 0.42;
}

function applyXray(runtime: ClientViewerRuntime | null, enabled: boolean): void {
  if (!runtime) return;
  for (const snapshot of runtime.materials) {
    if (!enabled) {
      snapshot.mesh.material = snapshot.material;
      continue;
    }
    const makeXray = (material: THREE.Material) => {
      const clone = material.clone();
      const label = String(clone.name || '').toLowerCase();
      clone.transparent = true;
      clone.opacity = /ручка|handle|петл|hinge|runner|metal|металл|samsung|микро/.test(label) ? 0.82 : 0.24;
      clone.depthWrite = false;
      clone.depthTest = true;
      clone.polygonOffset = true;
      clone.polygonOffsetFactor = 1;
      clone.polygonOffsetUnits = 1;
      clone.side = THREE.DoubleSide;
      if (clone instanceof THREE.MeshStandardMaterial || clone instanceof THREE.MeshPhysicalMaterial) {
        if (!/ручка|handle|петл|hinge|runner|metal|металл|samsung|микро/.test(label)) {
          clone.color.set('#dfe8e3');
          clone.emissive?.set('#000000');
          clone.metalness = 0;
          clone.roughness = 0.82;
        }
      }
      return clone;
    };
    snapshot.mesh.material = Array.isArray(snapshot.material)
      ? snapshot.material.map(makeXray)
      : makeXray(snapshot.material);
  }
}

function applyDimensions(runtime: ClientViewerRuntime | null, enabled: boolean): void {
  if (!runtime) return;
  runtime.dimensionsGroup.visible = enabled;
}

function applyOpening(runtime: ClientViewerRuntime | null, channel: 'facades' | 'drawers', opened: boolean): void {
  if (!runtime) return;
  runtime.openingGoal[channel] = opened ? 1 : 0;
}

export function updateOpeningAnimation(runtime: ClientViewerRuntime | null): void {
  if (!runtime) return;
  for (const channel of ['facades', 'drawers'] as const) {
    const delta = runtime.openingGoal[channel] - runtime.openingProgress[channel];
    if (Math.abs(delta) > 0.002) {
      runtime.openingProgress[channel] += delta * 0.085;
    } else {
      runtime.openingProgress[channel] = runtime.openingGoal[channel];
    }
  }
  for (const target of runtime.openingTargets) {
    const channel = target.kind === 'drawer' ? 'drawers' : 'facades';
    const delayed = Math.max(0, Math.min(1, (runtime.openingProgress[channel] - target.delay) / Math.max(0.01, 1 - target.delay)));
    const targetEase = easeOutCubic(delayed);
    if (target.kind === 'drawer') {
      const delta = target.axisWorld.clone().multiplyScalar(target.openMagnitude * targetEase);
      for (const state of target.closed) {
        const parent = state.object.parent;
        if (!parent) continue;
        const closedWorldPosition = parent.localToWorld(state.position.clone());
        const openedWorldPosition = closedWorldPosition.add(delta);
        parent.worldToLocal(openedWorldPosition);
        state.object.position.copy(openedWorldPosition);
        state.object.quaternion.copy(state.quaternion);
      }
      continue;
    }
    const rotation = new THREE.Quaternion().setFromAxisAngle(target.axisWorld, target.openMagnitude * targetEase);
    for (const state of target.closed) {
      const parent = state.object.parent;
      if (!parent) continue;
      const parentWorld = new THREE.Quaternion();
      parent.getWorldQuaternion(parentWorld);
      const parentInv = parentWorld.clone().invert();
      const closedWorldPosition = parent.localToWorld(state.position.clone());
      const openedWorldPosition = closedWorldPosition.sub(target.pivotWorld).applyQuaternion(rotation).add(target.pivotWorld);
      parent.worldToLocal(openedWorldPosition);
      state.object.position.copy(openedWorldPosition);
      const localRotation = parentInv.multiply(rotation).multiply(parentWorld);
      state.object.quaternion.copy(state.quaternion).premultiply(localRotation);
    }
  }
}

function easeOutCubic(value: number): number {
  const clamped = Math.max(0, Math.min(1, value));
  return 1 - Math.pow(1 - clamped, 3);
}

function createDimensionOverlay(): THREE.Group {
  const group = new THREE.Group();
  group.visible = false;
  return group;
}

function createModuleDimensionOverlays(model: THREE.Object3D, modelCenter: THREE.Vector3, radius: number): THREE.Object3D[] {
  const candidates: Array<{ box: THREE.Box3; size: THREE.Vector3; score: number }> = [];
  model.updateMatrixWorld(true);
  model.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const objectName = String(object.name || '').toLowerCase();
    const materialName = materialLabel(object.material).toLowerCase();
    if (!/детали:\s*фасады|facade|drawer_front/.test(objectName)) return;
    if (/ручка|handle|screw|bolt|конфирмат|опора|support|металл|metal|микро|samsung/.test(materialName)) return;
    const box = new THREE.Box3().setFromObject(object);
    const size = new THREE.Vector3();
    box.getSize(size);
    const dims = [size.x, size.y, size.z].sort((a, b) => a - b);
    if (dims[0] < 8 || dims[1] < 180 || dims[2] < 300 || dims[2] > 2450 || dims[1] > 2200) return;
    const score = size.x * size.y + size.y * size.z + size.x * size.z;
    candidates.push({ box, size, score });
  });
  return candidates
    .sort((a, b) => b.score - a.score)
    .slice(0, 12)
    .map(({ box, size }, index) => {
      const center = new THREE.Vector3();
      box.getCenter(center);
      const outward = new THREE.Vector3(center.x - modelCenter.x, 0, center.z - modelCenter.z);
      if (outward.lengthSq() < 1) outward.set(index % 2 === 0 ? 1 : -1, 0, index % 3 === 0 ? 1 : -1);
      outward.normalize();
      const labelPosition = center.clone().add(outward.multiplyScalar(Math.min(Math.max(radius * 0.018, 80), 180)));
      labelPosition.y = Math.min(box.max.y + 95, center.y + Math.max(size.y * 0.18, 110));
      const item = new THREE.Group();
      const helper = new THREE.Box3Helper(box, 0x1f7a55);
      const materials = Array.isArray(helper.material) ? helper.material : [helper.material];
      for (const material of materials) {
        material.transparent = true;
        material.opacity = 0.42;
      }
      item.add(helper);
      item.add(labelSprite(`${Math.round(size.x)}×${Math.round(size.y)}×${Math.round(size.z)} мм`, labelPosition, 'small'));
      return item;
    });
}

function labelSprite(text: string, position: THREE.Vector3, variant: 'normal' | 'small' = 'normal'): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 160;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(255,255,255,.92)';
    ctx.strokeStyle = 'rgba(31,122,85,.9)';
    ctx.lineWidth = 8;
    roundRect(ctx, 18, 28, 476, 104, 34);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#172033';
    ctx.font = variant === 'small' ? '750 34px Inter, system-ui, sans-serif' : '700 48px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 256, 82);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({ map: texture, depthTest: false, depthWrite: false });
  const sprite = new THREE.Sprite(material);
  sprite.position.copy(position);
  sprite.scale.set(variant === 'small' ? 390 : 520, variant === 'small' ? 122 : 162, 1);
  sprite.renderOrder = 20;
  return sprite;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function ClientEstimate({ value, disclosure }: { value: PublicSketchupClientResponse; disclosure: ReturnType<typeof publicSketchupEstimateDisclosure> }) {
  const groups = useMemo(() => Object.entries(value.estimate.partGroups || {}), [value.estimate.partGroups]);
  return (
    <div className="estimate-panel">
      <section className="summary-card">
        <div><span>Статус</span><strong>{disclosure.headline}</strong></div>
        <div><span>Строк</span><strong>{value.estimate.rows.length}</strong></div>
        <div><span>{value.estimate.unpricedSourceGroups?.count?'Стоимость позиций с ценой':'Итого клиенту'}</span><strong>{disclosure.showTotal ? `${money(value.estimate.total)} ₽` : '—'}</strong></div>
      </section>
      {disclosure.note ? <div className="estimate-disclosure" role="status">{disclosure.note}</div> : null}
      {/* 28.09.2026 — премиум-фаза 4: disclosure обещал «перечислены ниже» —
          теперь список действительно рендерится из unpricedSourceGroups.rows
          (API присылал их всегда). Цена «Не задана», а не 0 ₽. */}
      {unpricedRowsForClient(value.estimate.unpricedSourceGroups).length > 0 && (
        <section className="client-card unpriced-card" data-testid="public-unpriced-list">
          <h2>Позиции без цены · {unpricedRowsForClient(value.estimate.unpricedSourceGroups).length}</h2>
          <p className="unpriced-note">Мы сообщим стоимость этих позиций отдельно до подписания; в сумму выше они не входят.</p>
          <ul className="unpriced-list">
            {unpricedRowsForClient(value.estimate.unpricedSourceGroups).map((row) => (
              <li key={row.key}><strong>{row.name}</strong><span>{row.quantityDecimal && row.unit ? ` × ${row.quantityDecimal} ${row.unit}` : row.count !== null ? ` · количество: ${row.count}` : ' · количество уточняется'} · {row.reason === 'CUTTING_STOCK_AUTHORITY_REQUIRED' ? 'Нужно уточнить формат закупки и раскрой' : 'цена не задана'}</span></li>
            ))}
          </ul>
        </section>
      )}
      <section className="client-card">
        <h2>Смета</h2>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Раздел</th><th>Позиция</th><th>Артикул</th><th>Кол-во</th><th>Цена</th><th>Сумма</th></tr></thead>
            <tbody>
              {value.estimate.rows.map((row, index) => (
                <tr key={`${row.article || 'row'}-${index}`}>
                  <td>{row.kind?{material:'Материалы',hardware:'Фурнитура',operation:'Операции'}[row.kind]:'—'}</td>
                  <td><strong>{row.name || 'Позиция'}</strong><small>{row.note || row.confidence || ''}</small></td>
                  <td>{row.article || '—'}</td>
                  <td>{row.qty || '—'} {row.unit || ''}</td>
                  <td>{money(row.rateRub)}</td>
                  <td><strong>{money(row.totalRub)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="client-card">
        <h2>Что распознано из модели</h2>
        <div className="group-grid">
          {groups.map(([name, row]) => (
            <div className="group-pill" key={name}>
              <strong>{name}</strong>
              <span>{row.count ?? 0} шт · {row.areaM2 ?? '0'} м² · кромка {row.edgeM ?? '0'} м</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* 28.09.2026 — премиум-фаза 4: чистые хелперы клиентского экрана. */
export function composeDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('ru-RU');
}

export type PublicUnpricedRow = { key: string; name: string; count: number | null;
  quantityDecimal?: string; unit?: string; reason?: string };

export function unpricedRowsForClient(groups: { rows?: unknown[]; count?: number } | undefined | null): PublicUnpricedRow[] {
  if (!Array.isArray(groups?.rows)) return [];
  const seen = new Map<string, PublicUnpricedRow>();
  groups.rows.forEach((raw, index) => {
    if (!raw || typeof raw !== 'object') return;
    const row = raw as Record<string, unknown>;
    const hasName = typeof row.sourceName === 'string' && row.sourceName.trim().length > 0;
    const countRaw = Number(row.sourceOccurrenceCount);
    const count = Number.isFinite(countRaw) && countRaw > 0 ? countRaw : null;
    if (!hasName && count === null) return;
    const name = hasName ? (row.sourceName as string).trim() : 'Позиция без имени';
    const reason = typeof row.reason === 'string' && row.reason ? row.reason : undefined;
    const quantity = typeof row.quantityDecimal === 'string' && /^(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/.test(row.quantityDecimal)
      && /[1-9]/.test(row.quantityDecimal) ? row.quantityDecimal : undefined;
    const unit = typeof row.unit === 'string' && row.unit.trim() ? row.unit.trim() : undefined;
    // New exact quantities remain separate: never round metres or area into pieces.
    const key = quantity !== undefined || unit !== undefined ? JSON.stringify([name, reason, unit, index])
      : reason ? JSON.stringify([name,reason]) : name;
    const existing = seen.get(key);
    if (existing && existing.count !== null && count !== null) existing.count += count;
    else seen.set(key, { key, name, count, ...(reason ? {reason} : {}),
      ...(quantity ? {quantityDecimal:quantity} : {}), ...(unit ? {unit} : {}) });
  });
  return [...seen.values()].sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.name.localeCompare(b.name, 'ru'));
}

function money(value: unknown): string {
  if (value === null || value === undefined) return 'Не определено';
  const text = String(value).trim();
  if (!text) return 'Не определено';
  const num = Number(text);
  if (!Number.isFinite(num)) return 'Не определено';
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(num);
}

const clientCss = `
.client-page{min-height:100vh;box-sizing:border-box;padding:18px;background:radial-gradient(circle at 10% 0%,#fffaf1 0,#eef8f2 29%,#dbe8e2 100%);color:#172033;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
.client-page:before{content:"";position:fixed;inset:0;background:radial-gradient(circle at 72% 10%,rgba(38,111,81,.20),transparent 34%),linear-gradient(135deg,rgba(255,255,255,.45),rgba(255,255,255,0));pointer-events:none}
.client-header{position:relative;z-index:2;height:78px;display:flex;align-items:center;justify-content:space-between;gap:20px;padding:15px 24px;border:1px solid rgba(23,32,51,.10);border-radius:28px;background:linear-gradient(135deg,rgba(255,255,255,.88),rgba(246,251,248,.72));backdrop-filter:blur(24px);box-shadow:0 18px 54px rgba(44,59,74,.12),inset 0 1px 0 rgba(255,255,255,.76)}
.brand{display:flex;align-items:center;gap:13px;min-width:0}.brand-icon{display:grid;place-items:center;width:42px;height:42px;border-radius:16px;background:#266f51;color:#fff;box-shadow:0 12px 28px rgba(38,111,81,.24)}.brand-title{font-size:17px;font-weight:850}.brand-subtitle{max-width:62vw;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#687386;font-size:13px}.client-total{text-align:right}.client-total span{display:block;color:#687386;font-size:12px;text-transform:uppercase;letter-spacing:.12em}.client-total strong{font-size:24px}
.client-tabs{position:relative;z-index:2;display:flex;align-items:center;gap:10px;padding:14px 4px}.client-tabs button{display:inline-flex;align-items:center;gap:8px;border:1px solid rgba(38,111,81,.18);border-radius:999px;background:rgba(255,255,255,.78);backdrop-filter:blur(16px);padding:11px 18px;font-weight:850;color:#334155;box-shadow:0 10px 28px rgba(44,59,74,.08);cursor:pointer}.client-tabs button.active{background:#266f51;color:#fff;border-color:#266f51;box-shadow:0 16px 36px rgba(38,111,81,.22)}.readonly{margin-left:auto;display:inline-flex;align-items:center;gap:7px;color:#687386;font-size:13px}
.client-body{position:relative;z-index:2;height:calc(100vh - 142px);padding:0 0 2px}.model-panel,.estimate-panel{height:100%;min-height:0}.model-panel{position:relative;overflow:hidden;border-radius:34px;border:1px solid rgba(23,32,51,.10);background:linear-gradient(145deg,rgba(255,255,255,.78),rgba(225,240,233,.82));box-shadow:0 34px 100px rgba(44,59,74,.20),inset 0 1px 0 rgba(255,255,255,.78)}.model-panel:before{content:"";position:absolute;inset:0;background:radial-gradient(circle at 18% 18%,rgba(255,255,255,.92),transparent 34%),radial-gradient(circle at 78% 0%,rgba(38,111,81,.14),transparent 32%);pointer-events:none;z-index:1}.model-canvas{position:absolute;inset:0;z-index:0}.model-canvas canvas{display:block;width:100%;height:100%}.model-chrome{position:absolute;z-index:4;left:18px;right:18px;top:18px;display:flex;align-items:center;justify-content:space-between;gap:16px;border:1px solid rgba(23,32,51,.10);border-radius:24px;background:rgba(255,255,255,.72);backdrop-filter:blur(20px);padding:12px 14px;box-shadow:0 18px 48px rgba(44,59,74,.14)}.model-title{display:flex;flex-direction:column;gap:2px;padding:0 4px;min-width:190px}.model-title span{font-size:13px;color:#687386}.model-title strong{font-size:17px}.model-tools{display:flex;gap:9px;flex-wrap:wrap;justify-content:flex-end}.model-tools button{display:inline-flex;align-items:center;gap:7px;border:1px solid rgba(23,32,51,.12);border-radius:999px;background:rgba(255,255,255,.90);padding:10px 14px;color:#334155;font-weight:850;box-shadow:0 12px 30px rgba(44,59,74,.12);cursor:pointer}.model-tools button.active{background:#266f51;color:#fff;border-color:#266f51;box-shadow:0 16px 34px rgba(38,111,81,.24)}.model-tools button:disabled{cursor:not-allowed;opacity:.48;box-shadow:none}.model-hint{position:absolute;z-index:4;left:24px;bottom:24px;border:1px solid rgba(23,32,51,.10);border-radius:18px;background:rgba(255,255,255,.84);backdrop-filter:blur(16px);padding:11px 15px;color:#526071;font-size:13px;box-shadow:0 16px 38px rgba(44,59,74,.14)}
.estimate-panel{overflow:auto;display:grid;gap:16px;align-content:start}.client-card,.summary-card,.estimate-disclosure{border:1px solid rgba(23,32,51,.12);border-radius:24px;background:rgba(255,255,255,.86);box-shadow:0 18px 55px rgba(44,59,74,.12)}.client-card{padding:18px}.client-card h2{margin:0 0 14px;font-size:18px}.summary-card{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:1px;overflow:hidden}.summary-card div{padding:18px 20px;background:rgba(255,255,255,.64)}.summary-card span{display:block;color:#687386;font-size:12px;text-transform:uppercase;letter-spacing:.1em}.summary-card strong{display:block;margin-top:5px;font-size:22px}.print-pdf{border:1px solid rgba(38,111,81,.18);border-radius:999px;background:rgba(255,255,255,.78);padding:11px 18px;font-weight:850;color:#334155;cursor:pointer}
.composed-at{margin-top:3px;font-size:11px;letter-spacing:.04em;text-transform:none;color:#687386}
.unpriced-card{border-color:rgba(180,83,9,.30)}
.unpriced-note{margin:0 0 10px;color:#92400e;font-weight:650}
.unpriced-list{margin:0;padding:0 0 0 18px;color:#334155}
.unpriced-list li{margin:4px 0}
.estimate-disclosure{padding:14px 18px;border-color:rgba(180,83,9,.30);background:rgba(255,251,235,.92);color:#92400e;font-weight:650;line-height:1.45}.table-wrap{overflow:auto;border-radius:16px;border:1px solid rgba(23,32,51,.10)}table{width:100%;border-collapse:collapse;background:white}th,td{padding:11px 12px;border-bottom:1px solid #e5e9ef;text-align:left;vertical-align:top}th{position:sticky;top:0;background:#f7faf8;color:#526071;font-size:12px;text-transform:uppercase;letter-spacing:.08em}td small{display:block;margin-top:4px;color:#778397;line-height:1.35}.group-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:10px}.group-pill{border:1px solid rgba(38,111,81,.15);border-radius:16px;background:#f6fbf8;padding:12px}.group-pill strong{display:block}.group-pill span{display:block;margin-top:4px;color:#687386;font-size:13px}.client-state{display:inline-flex;align-items:center;gap:10px;padding:18px 22px}.client-state.error{color:#b42318}.spin{animation:client-spin 1s linear infinite}@keyframes client-spin{to{transform:rotate(360deg)}}@media(max-width:720px){.client-header{height:auto;align-items:flex-start;flex-direction:column}.client-total{text-align:left}.readonly{display:none}.client-body{height:calc(100vh - 184px);padding:0 10px 10px}.client-tabs{padding:10px}.summary-card{grid-template-columns:1fr}.brand-subtitle{max-width:88vw}}
`;
